import { createRef, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "@back4app2/react-components";
// import Pill from "../components/Pill";
import back4app2 from "../back4app2";
import { BACK4APP_DOT_COM_SITE_URL, NEW_PARSE_DASHBOARD_URL, CONTAINERS_DASHBOARD_URL, API_HTTP_URL } from "../settings";
import { AuthenticationError, AuthorizationError, ParseApp, NetworkError, PARSE_APP_DATABASE, ParseVersion, CreateParseAppError, AgentsSubscriptionAmountHardLimitError, AgentsSubscriptionChatMessagesHardLimitError } from "@back4app2/sdk";
import B4aLoader from "../components/B4aLoader/B4aLoader";
// import CreateParseAppAnimation from "../components/CreateParseAppAnimation";
// import { AnimatePresence } from "framer-motion";
// import { pushGTMEvent } from "../utils/gtm";
import { ReactComponent as BackendIconSVG } from "../assets/images/backend-icon.svg";
import { ReactComponent as WebAppIconSVG } from "../assets/images/web-app-icon.svg";
import { useAnimatedPlaceholder } from "../hooks/useAnimatedPlaceholder";

const PROMPT_MAX_LENGTH = 1000;
const PROMPT_CHAR_WARNING_THRESHOLD = 950;


interface CreateBackendState {
  appName: {
    value: string;
    errorMessage: string;
  };
  appFullStackPrompt: {
    value: string;
    errorMessage: string
  },
  database: PARSE_APP_DATABASE;
  createAppErrorMessage?: string;
  isCreatingApp: boolean;
  isNewAccount: boolean;
  enableCreateButton: boolean;
  onAppCreationError?: string;
  newApp?: ParseApp;
  parseVersionList?: ParseVersion[];
  showCaptcha: boolean;
  selectedAppToCreate: "backend" | "full-stack-web-app";
}

const INITIAL_STATE: CreateBackendState = {
  appName: {
    value: "",
    errorMessage: "",
  },
  appFullStackPrompt: {
    value: "",
    errorMessage: ""
  },
  database: PARSE_APP_DATABASE.NOSQL,
  isCreatingApp: false,
  isNewAccount: false,
  enableCreateButton: true,
  showCaptcha: false,
  selectedAppToCreate: "backend",
};

enum CreateBackendActionType {
  RESET,
  RESET_ON_ERROR,
  SET_FORM_FIELDS,
  VALIDATE_CREATE_APP,
  START_CREATING_APP,
  FINISH_CREATING_APP,
  SET_NEW_ACCOUNT,
  SET_BUTTON_STATUS,
  SET_SELECTED_APP_TO_CREATE
  // START_APP_CREATION_ANIMATION,
  // FINISH_APP_CREATION_ANIMATION,
}

const reset = () =>
  ({
    type: CreateBackendActionType.RESET,
  } as const);

const updateFormFields = (
  field: keyof CreateBackendState,
  value: string,
  errorMessage?: string
) =>
  ({
    type: CreateBackendActionType.SET_FORM_FIELDS,
    payload: {
      field,
      value,
      errorMessage,
    },
  } as const);

const validateCreateApp = (errorMessage: string) =>
  ({
    type: CreateBackendActionType.VALIDATE_CREATE_APP,
    payload: errorMessage,
  } as const);

const startCreatingApp = () =>
  ({
    type: CreateBackendActionType.START_CREATING_APP,
  } as const);

const setParseVersion = ({ newAccount, parseVersionList, showCaptcha }: {
  parseVersionList: ParseVersion[];
  newAccount: boolean;
  showCaptcha: boolean;
}) =>
  ({
    type: CreateBackendActionType.SET_NEW_ACCOUNT,
    payload: {
      parseVersionList,
      newAccount,
      showCaptcha,
    },
  } as const);

const setCreateButtonStatus = (enable: boolean) =>
  ({
    type: CreateBackendActionType.SET_BUTTON_STATUS,
    payload: enable,
  } as const);

const finishCreateApp = (errorMsg?: string, newApp?: ParseApp) =>
  ({
    type: CreateBackendActionType.FINISH_CREATING_APP,
    payload: {
      newApp,
      errorMsg,
    },
  } as const);

const setSelectedAppToCreate = (type: "backend" | "full-stack-web-app") =>
  ({
    type: CreateBackendActionType.SET_SELECTED_APP_TO_CREATE,
    payload: type,
  } as const);

// const startCreateAppAnimation = () =>
//   ({
//     type: CreateBackendActionType.START_APP_CREATION_ANIMATION,
//   } as const);

const resetOnErrorResponse = () => ({
  type: CreateBackendActionType.RESET_ON_ERROR
} as const)

type CreateBackendAction =
  | ReturnType<typeof reset>
  | ReturnType<typeof updateFormFields>
  | ReturnType<typeof validateCreateApp>
  | ReturnType<typeof startCreatingApp>
  | ReturnType<typeof setParseVersion>
  | ReturnType<typeof setCreateButtonStatus>
  // | ReturnType<typeof startCreateAppAnimation>
  | ReturnType<typeof finishCreateApp>
  | ReturnType<typeof resetOnErrorResponse>
  | ReturnType<typeof setSelectedAppToCreate>;

const reducer = (
  state: CreateBackendState = INITIAL_STATE,
  action: CreateBackendAction
) => {
  switch (action.type) {
    case CreateBackendActionType.RESET:
      return { ...INITIAL_STATE };
    case CreateBackendActionType.SET_FORM_FIELDS:
      if (
        action.payload.field === "appName" ||
        action.payload.field === "appFullStackPrompt"
      ) {
        return {
          ...state,
          [action.payload.field]: {
            value: action.payload.value,
            errorMessage: action.payload.errorMessage || "",
          },
        };
      }
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case CreateBackendActionType.VALIDATE_CREATE_APP:
      return {
        ...state,
        createAppErrorMessage: action.payload,
      };
    case CreateBackendActionType.START_CREATING_APP:
      return {
        ...state,
        isCreatingApp: true,
        createAppErrorMessage: undefined,
        onAppCreationError: undefined,
      };
    case CreateBackendActionType.FINISH_CREATING_APP:
      return {
        ...state,
        isCreatingApp: false,
        newApp: action.payload.newApp,
        onAppCreationError: action.payload.errorMsg,
      };
    case CreateBackendActionType.SET_NEW_ACCOUNT:
      return {
        ...state,
        isNewAccount: action.payload.newAccount,
        enableCreateButton: action.payload.showCaptcha ? !action.payload.newAccount : true,
        parseVersionList: action.payload.parseVersionList,
        showCaptcha: action.payload.showCaptcha,
      };
    case CreateBackendActionType.SET_BUTTON_STATUS:
      return {
        ...state,
        enableCreateButton: action.payload,
      };
    // case CreateBackendActionType.START_APP_CREATION_ANIMATION:
    //   return {
    //     ...state,
    //     isCreatingApp: true,
    //     onAppCreationError: undefined,
    //     enableCreateButton: false,
    //   };
    // case CreateBackendActionType.FINISH_APP_CREATION_ANIMATION:
    //   return {
    //     ...state,
    //     onAppCreationError: action.payload.errorMsg,
    //     newApp: action.payload.newApp,
    //     enableCreateButton: true,
    //     isCreatingApp:  !(action.payload && action.payload.errorMsg),
    //   };
    case CreateBackendActionType.RESET_ON_ERROR:
      return {
        ...state,
        appName: {
          value: "",
          errorMessage: "",
        },
        database: PARSE_APP_DATABASE.NOSQL,
        isCreatingApp: false,
        enableCreateButton: true,
      };
    case CreateBackendActionType.SET_SELECTED_APP_TO_CREATE:
      return {
        ...state,
        selectedAppToCreate: action.payload,
      };
  }
};

const CreateBackendApp = () => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const appNameInputRef = useRef<HTMLInputElement>(null);
  const recaptchaRef = createRef<ReCAPTCHA>();
  const isRequestPending = useRef(false);

  const appFullStackPromptRef = useRef<HTMLTextAreaElement>(null);

  const {
    appName,
    appFullStackPrompt,
    database,
    createAppErrorMessage,
    isNewAccount,
    enableCreateButton,
    isCreatingApp,
    onAppCreationError,
    newApp,
    parseVersionList,
    showCaptcha,
  } = state;

  // Prompt character count and counter class name
  const promptCharCount = appFullStackPrompt.value.length;
  const promptCounterClassName = appFullStackPrompt.errorMessage
    ? "text-error-red"
    : promptCharCount >= PROMPT_CHAR_WARNING_THRESHOLD
      ? "text-yellow-300"
      : "text-light-grey/70";

  const fullStackPlaceholderTexts = useMemo(
    () => [
      "a landing page to launch...",
      "a dashboard to track KPIs for...",
      "a data visualization tool for...",
      "an enterprise solution that...",
      "a MVP for my startup that...",
      "an admin panel to manage...",
      "a prototype to validate my...",
      "a customer portal where users can..."
    ],
  []);

  // Animated placeholder hook
  const animatedPlaceholder = useAnimatedPlaceholder({
    texts: fullStackPlaceholderTexts,
    baseText: "Let's build ",
    typingSpeed: 40,
    deletingSpeed: 20,
    pauseBeforeDelete: 2000,
    pauseBeforeType: 700,
    enabled: state.selectedAppToCreate === "full-stack-web-app" && !appFullStackPrompt.value.trim(),
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await back4app2.listParseVersion();
        dispatch(setParseVersion(response));
      } catch (e) {
        console.log("error in fetching parse versions");
      }
    })();
  }, []);

  const onClickCreateAppBtn = async () => {
    // pushGTMEvent('create_app_backend', true);
    dispatch(validateCreateApp(''));
    const appNameVal = appName.value.trim();
    const promptVal = appFullStackPrompt.value.trim();

    if(state.selectedAppToCreate === 'backend') {    
      if (!appNameVal || appNameVal.length < 3) {
        appNameInputRef.current?.focus();
        dispatch(resetOnErrorResponse());
        // dispatch(validateCreateApp('App name must be of at least 3 characters.'));
        dispatch(updateFormFields("appName", appNameVal, "App name must be of at least 3 characters."));
        return;
      }

      if (appNameVal.length > 250) {
        appNameInputRef.current?.focus();
        dispatch(resetOnErrorResponse());
        dispatch(updateFormFields("appName", appNameVal, "App name cannot be longer than 250 characters."));
        return;
      }
    } else {
      if (!promptVal || promptVal.length <= 0) {
        appFullStackPromptRef.current?.focus();
        // dispatch(validateCreateApp('Propmt cannot be empty.'));
        dispatch(updateFormFields("appFullStackPrompt", promptVal, "Prompt cannot be empty."));
        return;
      }
    }

    if (isRequestPending.current || isCreatingApp) {
      return;
    }

    const recaptchaRefVal = recaptchaRef.current?.getValue() || null;
    
    let parseVersion;
    if (!Array.isArray(parseVersionList) || parseVersionList.length === 0 && state.selectedAppToCreate === 'backend') {
      try {
        const response = await back4app2.listParseVersion();
        parseVersion = response.parseVersionList[0].version;
      } catch (err) {
        console.log('Unable to retrive parse versions, please contact us!');
        return dispatch(validateCreateApp('Unable to retrive parse versions, please contact us!'));
      }
    } else {
      parseVersion = parseVersionList[0].version;
    }
    
    isRequestPending.current = true;
    dispatch(startCreatingApp());
    try {
      if(state.selectedAppToCreate === 'backend') {
        const response = await back4app2.createParseApp(appNameVal, false, parseVersion, recaptchaRefVal, database);
        console.log('app created!')
        document.cookie = `newApp-${response.appId}=true; path=/; domain=back4app.com`;
        window.location.href = `${NEW_PARSE_DASHBOARD_URL}/apps/${response.id}/overview`;
        console.log('redirecting...');
        return; // Exit early to prevent any further execution
      } else if (state.selectedAppToCreate === "full-stack-web-app") {
        try {
          const agent = await back4app2.redirectToAgent(appFullStackPrompt.value)
          window.location.href = `${CONTAINERS_DASHBOARD_URL}/agents/${agent.id}`
        } catch (error) {
          if(
            error instanceof AgentsSubscriptionAmountHardLimitError ||
            error instanceof AgentsSubscriptionChatMessagesHardLimitError
          ) {
            window.location.href = `${CONTAINERS_DASHBOARD_URL}/agents`
          }
        }
      }
    } catch (e) {
      if (e instanceof NetworkError) {
        console.error('network error', e);
        dispatch(finishCreateApp('Network error while creating app.. Check your internet connection.'));
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (e instanceof CreateParseAppError) {
        try {
          let errorMsg = JSON.parse(e.message);
          dispatch(finishCreateApp(errorMsg.message));
        } catch (error) {
          dispatch(finishCreateApp(e.message));
        }
      } else {
        console.error('unexpected error creating agent', e);
        dispatch(finishCreateApp('Unexpected error while creating app.'));
      }
    } finally {
      isRequestPending.current = false;
    }
  };

  const onAppNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    const appNameVal = val.trim();
    let errorMsg = "";
    if (!appNameVal) {
      errorMsg = "Please enter app name to proceed.";
    }

    if (appNameVal.length < 3) {
      errorMsg = "App name must be of at least 3 characters.";
    }
    dispatch(updateFormFields("appName", val, errorMsg));
  };

  const onAppFullStackPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let val = e.target.value.slice(0, PROMPT_MAX_LENGTH);
    const promptVal = val.trim();
    let errorMsg = "";
    if (promptVal.length > PROMPT_MAX_LENGTH) {
      errorMsg = `Prompt limit exceeded. Maximum length is ${PROMPT_MAX_LENGTH} characters.`;
    }

    dispatch(updateFormFields("appFullStackPrompt", val, errorMsg));
  };

  const cleanFormFields = () => {  
    dispatch(updateFormFields("appFullStackPrompt", "", ""));
    dispatch(updateFormFields("appName", "", ""));
    dispatch(validateCreateApp(''));
    dispatch(finishCreateApp(undefined, undefined));
  };


  const resetToDefaultOnError = useCallback(
    () => {
      window.location.href = '/apps';
      // dispatch(resetOnErrorResponse());
    },
    [],
  )

  if (isCreatingApp) {
    return (
      <div className="flex justify-center items-center h-full bg-dark">
        <B4aLoader />
      </div>
    )
  } 

  return (
    <div className='flex h-full overflow-auto bg-dark glass-scroll'>
      <div className='flex flex-col justify-start max-w-4xl my-4 md:mx-6 lg:mx-auto py-8'>
        <h1 className='relative font-sora font-semibold text-[2.1875rem] text-white before:absolute before:left-0 before:-bottom-[0.2rem] before:w-6 before:h-1 before:bg-gradient-to-r before:rounded before:from-[#208AEC] before:to-cta-green'>
          How do you want to start ?
        </h1>
        <div className='flex gap-6 mx-auto mt-8 flex-col md:flex-row'>
          <div 
            className={`
              ${state.selectedAppToCreate === "backend" ? "glass-surface-selected" : "glass-surface"} 
              flex flex-col justify-center items-center gap-4 
              cursor-pointer w-full rounded-xl p-8 transition-all duration-300 relative
              ${state.selectedAppToCreate === "backend" 
                ? "" 
                : "hover:border-white/12 hover:bg-white/5"
              }
            `}
            onClick={() => {
              cleanFormFields();
              dispatch(setSelectedAppToCreate("backend"));
            }}
          >
            <BackendIconSVG 
              width='72px' 
              height='70px' 
              className={`transition-colors duration-300 ${state.selectedAppToCreate === "backend" ? "text-cta-green" : "text-white"}`}
            />
            <h1 className="text-[1.375rem] font-semibold text-white">Create my Backend</h1>
            <p className="text-center text-light-grey">Provision a ready‑to‑use backend in seconds: DB, API, Auth, Functions.</p>
          </div>
          <div 
            className={`
              ${state.selectedAppToCreate === "full-stack-web-app" ? "glass-surface-selected" : "glass-surface"} 
              flex flex-col justify-center items-center gap-4 
              cursor-pointer w-full rounded-xl p-8 transition-all duration-300 relative
              ${state.selectedAppToCreate === "full-stack-web-app" 
                ? "" 
                : "hover:border-white/12 hover:bg-white/5"
              }
            `}
            onClick={() => {
              cleanFormFields();
              dispatch(setSelectedAppToCreate("full-stack-web-app"));
            }}
          >
            <WebAppIconSVG 
              width='72px' 
              height='70px' 
              className={`transition-colors duration-300 ${state.selectedAppToCreate === "full-stack-web-app" ? "text-cta-green" : "text-white"}`}
            />
            <h1 className="text-[1.375rem] font-semibold text-white">Build with AI</h1>
            <p className="text-center text-light-grey">Create your Web App (with a backend) by chatting with AI.</p>
          </div>
        </div>
        <div className='glass-surface rounded-xl mx-auto p-8 mt-6 w-full'>
          <div className='font-bold text-[1.375rem] font-sora mb-5 text-center text-white'>
            {state.selectedAppToCreate === "backend" ? "Give your app a name" : "Describe what you want to build"}
          </div>
          <div className=''>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onClickCreateAppBtn();
              }}
            >
              { state.selectedAppToCreate === 'backend' && 
                <label className='inline-block w-full font-medium text-white'>
                  <input
                    type='text'
                    placeholder='Your app name'
                    autoFocus
                    ref={appNameInputRef}
                    className={`
                      glass-input-opaque mt-2 rounded-lg outline-none w-full 
                      inline-block focus:ring-2 focus:ring-[#208AEC]/60 
                      duration-200 border font-normal p-3 text-white
                      placeholder:text-light-grey/70
                      ${appName.errorMessage
                        ? "border-error-red focus:border-error-log-red"
                        : "border-white/14 focus:border-light-blue"
                      }
                    `}
                    onChange={onAppNameChange}
                    value={appName.value}
                    disabled={isCreatingApp}
                  />
                  <span className='text-error-red text-xs mt-1 block'>
                    {appName.errorMessage}
                  </span>
                </label>
              }
              {/* <div className=''>
                <div className='font-medium'>Database</div>
                <div className='flex space-x-4 mt-2'>
                  <label
                    className={`inline-block border py-2 px-4 rounded-lg cursor-pointer flex-1 duration-200 ${
                      database === PARSE_APP_DATABASE.NOSQL
                        ? "border-cta-green bg-dark"
                        : "border-light-grey"
                    }`}
                    onClick={() =>
                      dispatch(
                        updateFormFields("database", PARSE_APP_DATABASE.NOSQL)
                      )
                    }
                  >
                    NoSQL Database{" "}
                    <input
                      type='radio'
                      name='database'
                      id='database-nosql'
                      className='hidden'
                    />
                  </label>
                  <label
                    className={`border py-2 px-4 rounded-lg cursor-pointer flex-1 flex items-center gap-[0.625rem] duration-200 ${
                      database === PARSE_APP_DATABASE.POSTGRESQL
                        ? "border-cta-green bg-dark"
                        : "border-light-grey"
                    }`}
                    onClick={() =>
                      dispatch(
                        updateFormFields(
                          "database",
                          PARSE_APP_DATABASE.POSTGRESQL
                        )
                      )
                    }
                  >
                    PostgreSQL{" "}
                    <Pill
                      text='beta'
                      bgColor='cta-green/25'
                      borderColor='cta-green'
                    />
                    <input
                      type='radio'
                      name='database'
                      id='database-postgresql'
                      className='hidden'
                    />
                  </label>
                </div>
              </div> */}

              {isNewAccount && showCaptcha ? (
                <div className='mt-6 flex justify-center items-center p-4 bg-white/5 rounded-lg border border-white/8'>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    className={"recaptcha"}
                    sitekey='6LdFjSIUAAAAAMoeb8oXS-K6XMntKrLliUYWdGs2'
                    onChange={() => dispatch(setCreateButtonStatus(true))}
                    onExpired={() => dispatch(setCreateButtonStatus(false))}
                    onErrored={() => dispatch(setCreateButtonStatus(false))}
                  />
                </div>
              ) : null}

              <div className='mt-2 mb-4 text-error-red text-sm font-medium'>
                {createAppErrorMessage || onAppCreationError}
              </div>

              {/* describe your app */}
              { state.selectedAppToCreate === 'full-stack-web-app' && (
                <div className="flex flex-col my-6">
                  <div className="flex self-end mb-2">
                    <span
                      className={`text-xs font-medium mt-1 ${promptCounterClassName}`}
                      aria-live="polite"
                    >
                      {promptCharCount}/{PROMPT_MAX_LENGTH}
                    </span>

                  </div>
                  <textarea
                    className="glass-input-opaque w-full resize-none outline-none border-[1px] rounded-lg font-normal p-3 text-white placeholder:text-light-grey/70 focus:ring-2 focus:ring-[#208AEC]/60 focus:border-light-blue"  
                    rows={4}
                    value={appFullStackPrompt.value}
                    onChange={onAppFullStackPromptChange}
                    maxLength={PROMPT_MAX_LENGTH}
                    ref={appFullStackPromptRef}
                    placeholder={appFullStackPrompt.value ? "" : animatedPlaceholder}
                  />
                  <span className='text-error-red text-xs mt-1 block'>
                    {appFullStackPrompt.errorMessage}
                  </span>
                </div>
              )}

              <Button
                disabled={!enableCreateButton || isCreatingApp}
                type={!enableCreateButton || isCreatingApp ? 'default' : 'primary'}
                value={'Create'}
                className={`
                  w-full font-bold text-base h-full p-3 rounded-lg
                  transition-all duration-200
                  ${!enableCreateButton || isCreatingApp
                    ? "!bg-white/5 !text-white/50 cursor-not-allowed !border !border-white/10 hover:!bg-white/5"
                    : "!text-white !shadow-lg !shadow-[#22c55e]/20 hover:!shadow-[#22c55e]/30 !border !border-[#22c55e]/30"
                  }
                `}
                style={!enableCreateButton || isCreatingApp ? undefined : {
                  background: 'linear-gradient(to right, #22c55e, #16a34a)',
                }}
                buttonType="submit"
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBackendApp;
